/**
 * SourceBadge — trusted-source attribution pill used site-wide.
 *
 * Any benchmark figure that Corvana displays should carry a SourceBadge so the
 * user can trace the number back to its publisher (ABS, ATO, RBA, …).
 *
 * Usage:
 *   <SourceBadge sourceId="abs_8165" />
 *   <SourceBadge sourceId="rba_i2" compact />
 *   <SourceBadge sourceId="ato_benchmarks" refreshedAt="2026-04-15T00:00:00Z" />
 *
 * The component lazily fetches /api/benchmark-sources once (module-level cache)
 * so hover popovers are instant after the first render.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Info, ExternalLink, ShieldCheck, ShieldAlert } from 'lucide-react';
import { API_URL } from '@/config/apiConfig';

// Module-level cache — populated by the first SourceBadge that mounts.
let _sourcesCache = null;
let _sourcesPromise = null;

async function loadSources() {
  if (_sourcesCache) return _sourcesCache;
  if (_sourcesPromise) return _sourcesPromise;
  _sourcesPromise = fetch(`${API_URL}/api/benchmark-sources`)
    .then(r => r.ok ? r.json() : { sources: [] })
    .then(d => {
      _sourcesCache = Object.fromEntries((d.sources || []).map(s => [s.id, s]));
      return _sourcesCache;
    })
    .catch(() => ({}));
  return _sourcesPromise;
}

function timeAgo(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const CONFIDENCE_STYLE = {
  high:   { color: 'text-emerald-400', icon: ShieldCheck, label: 'Official' },
  medium: { color: 'text-[#4DA6FF]',   icon: ShieldCheck, label: 'Industry' },
  low:    { color: 'text-amber-400',   icon: ShieldAlert, label: 'Estimate' },
};

export function SourceBadge({ sourceId, refreshedAt, compact = false, className = '' }) {
  const [source, setSource] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let mounted = true;
    loadSources().then(map => {
      if (mounted) setSource(map[sourceId] || null);
    });
    return () => { mounted = false; };
  }, [sourceId]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!source) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] text-slate-500 ${className}`}
        data-testid={`source-badge-${sourceId}-loading`}
      >
        <Info className="w-3 h-3" /> {sourceId}
      </span>
    );
  }

  const conf = CONFIDENCE_STYLE[source.confidence] || CONFIDENCE_STYLE.medium;
  const Icon = conf.icon;
  const short = source.organisation?.split('—')[0]?.trim() || source.name;

  return (
    <span ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-[2px] rounded-md bg-black/5 dark:bg-white/10 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:bg-black/10 dark:hover:bg-white/15 transition-colors border border-black/5 dark:border-white/5"
        data-testid={`source-badge-${sourceId}`}
      >
        <Icon className={`w-3 h-3 ${conf.color}`} />
        <span className="truncate max-w-[120px]">{compact ? conf.label : short}</span>
      </button>

      {open && (
        <div
          className="absolute z-50 top-full left-0 mt-2 w-80 rounded-2xl p-4 text-xs bg-white/90 dark:bg-[#1C1C1E]/90 border border-black/10 dark:border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
          data-testid={`source-badge-${sourceId}-popover`}
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="font-semibold tracking-tight text-slate-900 dark:text-white leading-snug">{source.name}</div>
            <span className={`px-1.5 py-[2px] rounded font-mono tracking-wide text-[10px] uppercase flex-shrink-0 border ${
              source.confidence === 'high' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' :
              source.confidence === 'medium' ? 'bg-[#007AFF]/10 text-[#007AFF] dark:text-[#0A84FF] border-[#007AFF]/20' :
              'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
            }`}>
              {conf.label}
            </span>
          </div>
          <dl className="space-y-1.5 text-slate-600 dark:text-slate-400">
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500 dark:text-slate-500">Publisher</dt>
              <dd className="text-right text-slate-700 dark:text-slate-200 font-medium">{source.organisation}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500 dark:text-slate-500">Cadence</dt>
              <dd className="text-right font-mono text-[11px] text-slate-700 dark:text-slate-200">{source.refresh_cadence}</dd>
            </div>
            {source.licence && (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500 dark:text-slate-500">Licence</dt>
                <dd className="text-right text-slate-700 dark:text-slate-200">{source.licence}</dd>
              </div>
            )}
            {refreshedAt && (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500 dark:text-slate-500">Last refresh</dt>
                <dd className="text-right text-slate-700 dark:text-slate-200">{timeAgo(refreshedAt)}</dd>
              </div>
            )}
          </dl>
          {source.disclaimer && (
            <div className="text-slate-500 dark:text-slate-400 italic mt-3 pt-3 border-t border-black/5 dark:border-white/10 leading-relaxed">
              {source.disclaimer}
            </div>
          )}
          {source.url && (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#007AFF] dark:text-[#0A84FF] hover:underline mt-3 font-medium"
              data-testid={`source-badge-${sourceId}-link`}
            >
              View source <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <div className="mt-3 pt-3 border-t border-black/5 dark:border-white/10 text-[10px] text-slate-500 dark:text-slate-500 leading-relaxed">
            Educational insights only — not financial, tax, or legal advice.
          </div>
        </div>
      )}
    </span>
  );
}

export default SourceBadge;
